import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

import { PageSeo } from 'components/common';
import { LandingPageBlocks } from 'components/landing-page';
import { Button, Link } from 'components/ui';
import { getDocumentToReactComponentsOptions } from 'helpers/LandingPageHelpers';
import * as SeoHelpers from 'helpers/SeoHelpers';
import { parseAbsoluteUrl } from 'helpers/UrlHelpers';
import { useTranslation } from 'hooks/common/useTranslation';
import { useBlogPageProps } from 'hooks/useBlogPageProps';

import { ContentfulLandingPageType, type IContentfulBetaLandingPage } from 'types';

import styles from './BetaTemplate.module.scss';

interface Props {
  isMobilePreview?: boolean,
  data: IContentfulBetaLandingPage
}

const BetaTemplate = (props: Props) => {

  const {
    data,
    isMobilePreview
  } = props;

  // Hooks

  const { t } = useTranslation();

  // Props

  const {
    slug,
    title,
    blocks,
    seoTitle,
    seoDescription,
    footer
  } = data || {};

  const pageSeoProps = SeoHelpers.getBlogLandingPageSeo(t, { title: seoTitle, description: seoDescription }, isMobilePreview);

  // Context Hooks

  useBlogPageProps({ slug, title });

  // Render

  return (
    <>
      <PageSeo {...pageSeoProps} />
      <LandingPageBlocks
        blocks={blocks}
        templateType={ContentfulLandingPageType.Beta}
        isMobilePreview={isMobilePreview}
      />
      <BetaTemplateFooter
        footer={footer}
        isMobilePreview={isMobilePreview}
      />
    </>
  );
};

const BetaTemplateFooter = (props: {footer: IContentfulBetaLandingPage['footer'], isMobilePreview: boolean}) => {
  const { footer, isMobilePreview } = props;
  const {
    title,
    disclaimer,
    regulation,
    button
  } = footer || {};

  const { link: buttonLink, text: buttonText } = button || {};
  const parsedUrl = React.useMemo(() => (buttonLink ? parseAbsoluteUrl(buttonLink) : null), [buttonLink]);
  const { isInternal, mobilePreviewPathname, pathname } = parsedUrl || {};
  const { json: titleJson } = title || {};
  const { json: disclaimerJson } = disclaimer || {};
  const { json: regulationJson } = regulation || {};

  const options = React.useMemo(() => getDocumentToReactComponentsOptions({ isMobilePreview }), [isMobilePreview]);
  const href = isMobilePreview ? mobilePreviewPathname ? mobilePreviewPathname.toString() : pathname : pathname;

  return (
    <div className={styles.footer}>
      <div className={styles.footerInner}>
        {title && <div className={styles.title}>{documentToReactComponents(titleJson, options)}</div>}
        {
          (buttonLink && buttonText) && (
            <>
              {isInternal ? (
                <Link passHref href={href}>
                  <Button className={styles.button} variant="primary" tag="a">{buttonText}</Button>
                </Link>
              ) : (
                <Button
                  variant="primary"
                  tag="a"
                  className={styles.button}
                  href={buttonLink}
                  target={isMobilePreview ? '_self' : '_blank'}
                  rel="noreferrer"
                >
                  {buttonText}
                </Button>
              )}
            </>
          )
        }
        {regulation && <div className={styles.regulation}>{documentToReactComponents(regulationJson, options)}</div>}
        {disclaimer && <div className={styles.disclaimer}>{documentToReactComponents(disclaimerJson, options)}</div>}
      </div>
    </div>
  );
};

export default BetaTemplate;
